import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Table from '../../../blocks/table/TableClient';
import Typography from '../../../components/typography/Typography';
import { SelectedGoods, DeliveryLocation } from '../../../models/Goods';
import { DeleteIcon } from '../../../assets/icons/DeleteIcon';
import { DialogBody } from '../../../stateManagement/reducers/confirmDialogReducer';
import Button from '../../../components/button/Button';
import { useConfirmationDialog } from '../../../hooks/useConfirmationDialog';
import AutoComplete from '../../../components/auto-complete/AutoComplete';
import TextField from '../../../components/text-field/TextField';
import { DropPointDTO, LocationDTO, StockDTO } from '../../../api/api';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import WarehouseService from '../../../services/WarehouseService';
import { log } from '../../../utils/logging/log';
import NotificationService from '../../../services/NotificationService';
import { useSelector } from 'react-redux';
import { selectUserProfile } from '../../../stateManagement/reducers/userProfileReducer';
import { useOrderSelectedGoodsList } from '../hooks/useOrderSelectedGoodsList';

const clearGoodsDialog: DialogBody = {
  headerText: 'Vil du fjerne alle varer i din kurv?',
  bodyText: 'Du er ved at fjerne alle varer fra din kurv. Er du sikker på, at du vil fortsætte?',
  declineButtonText: 'Fortryd',
  confirmButtonText: 'Bekræft'
};

interface Props {
  selectedGoods: SelectedGoods[];
  className?: string;
  showAvailable?: boolean;
  showDeliverTo?: boolean;
  selectedDeliveryLocation?: DeliveryLocation;
  setDeliveryLocation?: (delivery: DeliveryLocation) => void;
  deliveryOptions?: DeliveryLocation[];
  clearSelectedGoods?: () => void;
  renderFullHeight?: boolean;
  taskLocation?: LocationDTO;
  stockErrorCallback: (hasError: boolean) => void;
}

enum DropPointOptions {
  NEAREST = 'NEAREST',
  SPECIFIC = 'SPECIFIC'
}

const OrderSelectedGoodsList = (props: Props) => {
  const {
    selectedGoods,
    className,
    selectedDeliveryLocation,
    setDeliveryLocation,
    deliveryOptions,
    clearSelectedGoods,
    renderFullHeight = false,
    taskLocation,
    stockErrorCallback
  } = props;

  const { getConfirmation } = useConfirmationDialog();
  const [dropPointOption, setDropPointOption] = useState<DropPointOptions>(DropPointOptions.SPECIFIC);
  const [loading, setLoading] = useState(false);
  const [nearestDropPoint, setNearestDropPoint] = useState<DropPointDTO>();
  const favoriteDropPoint = useSelector(selectUserProfile).userProfile.favoriteDropPoint;
  const myDropPoint = useSelector(selectUserProfile).userProfile.defaultDropPoint;
  const [stock, setStock] = useState<StockDTO[]>([]);

  const { tableInstance } = useOrderSelectedGoodsList(stockErrorCallback, stock);

  useEffect(() => {
    setLoading(true);
    WarehouseService.getMainWarehouseInventoryByItems(selectedGoods.map((good) => good.itemId ?? ''))
      .then((response) => {
        setStock(response);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const sortedDeliveryOptions = useMemo(() => {
    const sorted =
      deliveryOptions?.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase())) ?? [];

    const customOptions = [];

    myDropPoint && customOptions.push({ id: myDropPoint.warehouseId, name: `Dit dropsted: ${myDropPoint.city}` });
    favoriteDropPoint &&
      customOptions.push({ id: favoriteDropPoint?.warehouseId, name: `Favorit dropsted: ${favoriteDropPoint?.city}` });

    if (customOptions.length > 0) customOptions.push({ id: '-1', name: '_____________________' });

    return [...customOptions, ...sorted] as DeliveryLocation[];
  }, [deliveryOptions, favoriteDropPoint, myDropPoint]);

  const selectedCount = useMemo(() => {
    return selectedGoods.reduce((acc, goods) => acc + (goods?.qty ?? 1), 0);
  }, [selectedGoods]);

  useEffect(() => {
    getNearestDropPoint();
  }, [taskLocation]);

  useEffect(() => {
    if (dropPointOption === DropPointOptions.NEAREST && setDeliveryLocation && taskLocation) {
      setDeliveryLocation({ id: nearestDropPoint?.warehouseId ?? '', name: nearestDropPoint?.name ?? '' });
    }
  }, [dropPointOption, nearestDropPoint, setDeliveryLocation, taskLocation]);

  const getNearestDropPoint = () => {
    if (!taskLocation?.latitude || !taskLocation?.longitude) return;
    setLoading(true);

    WarehouseService.getDropPointsByDistance(taskLocation.latitude, taskLocation.longitude)
      .then((data) => {
        setNearestDropPoint(data[0]);
      })
      .catch((error) => {
        log(error);
        NotificationService.error('Kunne ikke hente nærmeste dropsted');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClearSelectedGoods = () => {
    getConfirmation(clearGoodsDialog).then((res) => {
      if (res === 'confirm') {
        clearSelectedGoods && clearSelectedGoods();
      }
    });
  };

  return (
    <>
      <Header>
        {selectedDeliveryLocation && (
          <>
            <Typography variant="h5" fontWeight="bold">
              Varer sendes til:
            </Typography>
            {taskLocation ? (
              <FormControl>
                <RadioGroup
                  value={dropPointOption}
                  onChange={(e) => setDropPointOption(e.target.value as DropPointOptions)}
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                >
                  <SpecificDropPointContainer>
                    <FormControlLabel
                      value={DropPointOptions.SPECIFIC}
                      control={<Radio />}
                      label={dropPointOption === DropPointOptions.NEAREST && 'Specifikt leveringssted'}
                    />
                    {dropPointOption === DropPointOptions.NEAREST ? (
                      ''
                    ) : (
                      <>
                        {sortedDeliveryOptions && setDeliveryLocation ? (
                          <AutoCompleteContainer>
                            <AutoComplete
                              getOptionDisabled={(option) => option.id === '-1'}
                              onChange={(_, value, reason) => setDeliveryLocation(value as DeliveryLocation)}
                              options={sortedDeliveryOptions}
                              value={selectedDeliveryLocation}
                              renderInput={(params) => <TextField {...params} label={'Specifikt leveringssted'} fullWidth />}
                              getOptionLabel={(option) => `${option.name}`}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              noOptionsText={'Kan ikke hente dropsteder'}
                              disableClearable
                            />
                          </AutoCompleteContainer>
                        ) : (
                          <Typography variant="h5" fontWeight="bold">
                            ({selectedDeliveryLocation.id}) {selectedDeliveryLocation.name}
                          </Typography>
                        )}
                      </>
                    )}
                  </SpecificDropPointContainer>
                  <NearestDropPointContainer>
                    <FormControlLabel
                      value={DropPointOptions.NEAREST}
                      control={<Radio />}
                      label={`Nærmeste dropsted (${loading ? 'Henter..' : nearestDropPoint?.city})`}
                    />
                  </NearestDropPointContainer>
                </RadioGroup>
              </FormControl>
            ) : (
              <Typography variant="h5" fontWeight="bold">
                ({selectedDeliveryLocation.id}) {selectedDeliveryLocation.name}
              </Typography>
            )}
          </>
        )}
        {clearSelectedGoods && (
          <Button variant="secondary" onClick={handleClearSelectedGoods} disabled={selectedCount === 0}>
            <DeleteIcon size="20px" />
            Ryd kurv {selectedCount > 0 ? `(${selectedCount})` : ''}
          </Button>
        )}
      </Header>
      <StyledTable
        tableInstance={tableInstance}
        loading={loading}
        alwaysShowSort
        noDataText="Ingen valgte varer. Vælg fra listen over mulige varer."
        className={className}
        renderFullHeight={renderFullHeight}
      />
    </>
  );
};

const StyledTable = styled(Table)`
  height: 60%;
  tbody {
    overflow-y: scroll;
  }
  &.table-outer-container {
    margin: 0;
    padding: 0;
  }
  .table-content-container {
    margin: 0;
    padding: 0;
  }
`;

const AutoCompleteContainer = styled.div`
  width: 400px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: ${(props) => props.theme.spacing(3)} 0;
`;

const NearestDropPointContainer = styled.div``;

const SpecificDropPointContainer = styled.div`
  display: flex;
  width: 350px;
  margin-right: 40px;
`;

export default OrderSelectedGoodsList;
